import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { API_PATIENT_NOTES } from 'services/dataService/ccmResources';
import { showResult } from 'infrastructure/hooks/utils';
import { DateFormatter, isRpmModule } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';

import type { DateRange, SortState } from 'infrastructure/types';
import type { ModuleType } from 'infrastructure/enums';
import type {
  IPartialPaginationMeta,
  IClinicNotesResponse,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  module: ModuleType;
  meta: IPartialPaginationMeta;
  dateRange: DateRange | [];
  documentationTimeSort?: SortState;
}

type TFilter = {
  patientGuid: string;
  startTime?: string;
  endTime?: string;
  module: ModuleType;
  documentationTimeSort?: string;
} & IPartialPaginationMeta;

export const getClinicalNotesFn = async (filter: TFilter) => {
  let url = API_PATIENT_NOTES;

  if (isRpmModule(filter.module)) url = `core/patient/clinical-notes`;

  const { data, error } = await dataService.getList<IClinicNotesResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getClinicalNotesKey = (filter: TFilter) => [
  'getClinicalNotes',
  filter,
];

export const CLINICAL_NITES_PER_PAGE = 5;

export const useGetClinicalNotes = ({
  patientGuid,
  module,
  meta,
  dateRange,
  documentationTimeSort,
}: Props) => {
  const [startTime, endTime] = dateRange ?? [];

  const filter: TFilter = {
    ...meta,
    patientGuid,
    module,
    items: CLINICAL_NITES_PER_PAGE,
    startTime: DateFormatter({
      date: startTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    endTime: DateFormatter({
      date: endTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    documentationTimeSort: documentationTimeSort?.toUpperCase(),
  };

  const {
    data: clinicalNotes,
    isLoading,
    isFetching,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: getClinicalNotesKey(filter),
    queryFn: async () => getClinicalNotesFn(filter),
    enabled: Boolean(startTime) && Boolean(endTime),
  });

  return {
    clinicalNotes,
    isLoading: isLoading || isFetching || isRefetching,
    isFetching,
    ...other,
  };
};
